import { useTranslation } from "react-i18next";
import Text from "components/Text";
import { Option, TypeEnum, filterOption } from "models/filterOption";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useReports } from "hooks/useReports";
import Form from "react-bootstrap/Form";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "./multiSelect.scss";
import { useRole } from "hooks/useRole";
import { FilterType } from "components/Reports/reportAssets/assets";
import { MultiSelectProps } from "./multiSelect.types";
import useFilterOptions from "./useFilterOptions";
import { Download, Plus, PlusSquare, XLg } from "react-bootstrap-icons"; // eslint-disable-line

const MultiSelect = (props: MultiSelectProps) => {
  const { t } = useTranslation();
  const {
    filterType,
    reportDate,
    setReportDate,
    updateDate,
    setUpdateDate,
    registrationDate,
    setRegistrationDate,
    lastReportUpdate,
    setLastReportUpdate,
    filtersOptions,
    setFiltersOptions,
    filterOptionInit,
    runFilter,
    setRunFilter,
    setChosenFilter,
    chosenFilter,
    submitFilter,
    dbRequest,
    filterOptionsObject,
  } = props;

  const role = useRole();
  const [message, setMessage] = useState("");
  const { filtersOptionsToDBFormat } = useFilterOptions(filterType);
  const { selectedRole } = useReports();

  const isReportType = filterType === FilterType.reportType;
  const isFilterOnServerSide =
    filterType === FilterType.volunteersType ||
    filterType === FilterType.controllersType;

  const handleRootClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent click event from propagating
  };

  const setDatesState = (filterName: string) => {
    const dateStateMap = {
      reportDate: setReportDate,
      updateDate: setUpdateDate,
      registrationDate: setRegistrationDate,
      lastReportUpdate: setLastReportUpdate,
    };
    const setDateState = dateStateMap[filterName];
    if (setDateState) {
      setDateState(null);
    }
  };

  const handleDateValue = (selectedFilterName) => {
    const dateValueMap = {
      reportDate,
      updateDate,
      registrationDate,
      lastReportUpdate,
    };

    return dateValueMap[selectedFilterName];
  };

  const deleteFilter = (id, filterName) => {
    setDatesState(filterName);
    const filters = {
      ...filtersOptions,
      options: [
        ...filtersOptions.options.map((opt: Option) => {
          if (opt.id === id) {
            const deletedOptoin = opt;
            deletedOptoin.isSelected = false;
            deletedOptoin.value = "";
            return deletedOptoin;
          }
          return opt;
        }),
      ].filter((opt: Option) => opt?.id !== null),
    };
    setFiltersOptions(filters);
    submitFilter(filters);
  };

  const clearFilter = useCallback(() => {
    if (!filterOptionsObject) return;

    filterOptionInit();
    setRunFilter(false);

    const filters = {
      ...filterOptionsObject,
      options: filterOptionsObject.options.map((option) => ({
        ...option,
        isSelected: false,
      })),
    };

    const resetChosenFilter = { ...chosenFilter };
    Object.keys(resetChosenFilter).forEach((key) => {
      resetChosenFilter[key] = null;
    });

    setChosenFilter(resetChosenFilter);
    submitFilter(filters);

    if (isReportType) {
      setReportDate(null);
      setUpdateDate(null);
    } else {
      setRegistrationDate(null);
      setLastReportUpdate(null);
    }
  }, [
    chosenFilter,
    filterOptionInit,
    filterOptionsObject,
    isReportType,
    setChosenFilter,
    setLastReportUpdate,
    setRegistrationDate,
    setReportDate,
    setRunFilter,
    setUpdateDate,
    submitFilter,
  ]);

  const renderNotSelectedFilters = () => {
    return filtersOptions?.options
      .filter((filter) => !filter.isSelected)
      .map(({ field, label }) => ({
        value: field,
        label,
      }));
  };

  useEffect(() => {
    if (selectedRole !== null) {
      clearFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  const onChangeFilterOptions = (
    optionFinder: number,
    option,
    reset = false
  ) => {
    setFiltersOptions((prev: filterOption) => ({
      ...prev,
      options: [
        ...prev.options.map((opt: Option, index) => {
          if (index === optionFinder) {
            let optionData;
            switch (opt.type) {
              case TypeEnum.Enum:
                optionData = option.value;
                break;
              case TypeEnum.String:
                optionData = option.target.value;
                break;
              default:
                optionData = option;
                break;
            }
            const updatedOption = opt;
            if (reset) {
              updatedOption.value = 0;
            } else {
              updatedOption.value = optionData;
            }
            return updatedOption;
          }
          return opt;
        }),
      ],
    }));
  };

  const handleDateChange = (date: Date, selectedFilterName, filterId) => {
    const filterIndex = filtersOptions?.options.findIndex(
      (filter) => filter.id === filterId
    );
    if (filterIndex === -1) {
      return null;
    }

    const selectedDate = date;
    onChangeFilterOptions(filterIndex, selectedDate);

    const setterMap = {
      reportDate: setReportDate,
      updateDate: setUpdateDate,
      registrationDate: setRegistrationDate,
      lastReportUpdate: setLastReportUpdate,
    };
    const setter = setterMap[selectedFilterName];
    if (setter) {
      setter(selectedDate);
    }
    return null;
  };

  const getCurrentLabel = (optionFinder) => {
    const isVerifyField =
      filtersOptions?.options[optionFinder].field === "isVerify";

    return filtersOptions?.options[optionFinder]?.options?.find((opt) => {
      let selectedOptionValue = filtersOptions?.options[optionFinder].value;
      if (isVerifyField) {
        selectedOptionValue = selectedOptionValue ? 1 : 0;
      }
      return opt.value === selectedOptionValue;
    });
  };

  const renderFilterComponent = (filterId: number, type: TypeEnum) => {
    const selectedFilterName = filtersOptions?.options?.find(
      (filter) => filter.id === filterId
    ).field;

    const optionFinder = filtersOptions?.options.findIndex(
      (filter) => filter.id === filterId
    );

    if (type === 2) {
      return (
        <>
          <Text className="defualt-padding-left">{t("contains")}:</Text>

          <Select
            value={getCurrentLabel(optionFinder)}
            className="basic-single"
            classNamePrefix="select"
            placeholder={t(selectedFilterName)}
            isRtl
            name={`filter_${filterId}_subcategory`}
            options={filtersOptions?.options[optionFinder]?.options}
            onChange={(option) => onChangeFilterOptions(optionFinder, option)}
          />
        </>
      );
    }
    if (type === 1) {
      return (
        <DatePicker
          placeholderText={t("selectDate")}
          selected={handleDateValue(selectedFilterName)}
          onChange={(date: Date) => {
            handleDateChange(date, selectedFilterName, filterId);
          }}
          dateFormat="dd/MM/yyyy"
          className="custom-datepicker"
        />
      );
    }
    if (type === 3) {
      return (
        <>
          <Text className="defualt-padding-left">{t("contains")}:</Text>
          <Form.Control
            value={filtersOptions.options[optionFinder].value?.toString() || ""}
            onChange={(event) => onChangeFilterOptions(optionFinder, event)}
            placeholder={t(selectedFilterName)}
            aria-label={t(selectedFilterName)}
            aria-describedby={`text-input for ${selectedFilterName}`}
          />
        </>
      );
    }
    return null;
  };

  const addFilter = () => {
    setRunFilter(false);
    setFiltersOptions((prev: filterOption) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          id: null,
          field: undefined,
          value: "",
          options: undefined,
          type: undefined,
          isSelected: true,
        },
      ],
    }));
  };

  const updateFilter = (selectedFilterDropDown, filterName) => {
    setRunFilter(false);
    setFiltersOptions((prev: filterOption) => ({
      ...prev,

      options: [
        ...prev.options
          .map((opt: Option) => {
            if (filterName !== undefined && opt.field === filterName) {
              const delteFilterObj = opt;
              delteFilterObj.isSelected = false;
              delteFilterObj.value = "";
              return delteFilterObj;
            }
            if (opt.field === selectedFilterDropDown.value) {
              const updateOption = opt;
              updateOption.isSelected = true;
              return updateOption;
            }
            return opt;
          })
          .filter((opt) => opt.id !== null),
      ],
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    setRunFilter(true);
    submitFilter(filtersOptions);
  };

  const saveFilterToDB = async () => {
    const dataforDb = {
      ...filtersOptionsToDBFormat(filtersOptions),
      userRoleCode: role,
    };

    try {
      await dbRequest.addOrUpdateFilter(dataforDb).unwrap();
      if ("id" in dataforDb) {
        setMessage(t("UpdatedSuccessfully"));
      } else {
        setMessage(t("AddedSuccessfully"));
      }
    } catch (err) {
      setMessage(`${t("FailedToSaveFilter")}:${err}`);
    }

    setTimeout(() => {
      setMessage("");
    }, 2000);
  };

  const isEmptyOption = filtersOptions?.options.some(
    (filter: Option) => filter.id === null
  );
  const isSomeSelected = filtersOptions?.options.some(
    (filter: Option) => filter.isSelected && filter.value !== ""
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
    <form
      onClick={handleRootClick}
      onSubmit={handleSubmit}
      className="formContainer"
    >
      {!isSomeSelected && <Text color="grey">{t("NoFilterChosen")}</Text>}
      {filtersOptions?.options
        .filter((filter) => filter.isSelected)
        .map((filter) => (
          <div
            className="flexContainerMultySelect space-between"
            key={filter.id}
          >
            <div className="flexContainer">
              <button
                type="button"
                onClick={() => deleteFilter(filter.id, filter.field)}
                className="defualt-padding-left"
              >
                <XLg size={18} />
              </button>
              <Select
                className="basic-single"
                classNamePrefix="select"
                placeholder={t(filter.field) || t("select")}
                isRtl
                name={`filter_by${filter.field}`}
                options={renderNotSelectedFilters()}
                onChange={(option) => updateFilter(option, filter.field)}
              />
            </div>
            <div className="flexContainer">
              {renderFilterComponent(filter.id, filter.type)}
            </div>
          </div>
        ))}
      <div className="flexContainerMultySelect">
        <button type="button" onClick={addFilter} disabled={isEmptyOption}>
          <Plus size={18} />
          {t("addFilter")}
        </button>

        <button
          type="submit"
          className="roundButton"
          disabled={!isSomeSelected}
        >
          {t("filter")}
        </button>
      </div>
      <div className="flexContainerMultySelect">
        <button
          type="button"
          onClick={saveFilterToDB}
          className="felx-container"
          disabled={
            isReportType
              ? filtersOptions?.name === undefined || filtersOptions.name === ""
              : filtersOptions?.filterName === undefined ||
                filtersOptions.filterName === ""
          }
        >
          {dbRequest?.isSaving ? t("savingProcess") : t("SaveFilter")}
          &nbsp;&nbsp;
          <Download size={12} />
        </button>

        <Form.Control
          value={filtersOptions?.name || filtersOptions?.filterName || ""}
          placeholder={t("filterName")}
          aria-label={t("filterName")}
          aria-describedby={`choose saved filterName"`}
          onChange={(event) => {
            const { value } = event.target;

            setFiltersOptions((prev) => ({
              ...prev,
              ...(isFilterOnServerSide
                ? { filterName: value }
                : { name: value }),
            }));
          }}
          className="autoWidth"
        />
      </div>
      {message}
      <div />
      {isSomeSelected && (
        <button
          type="button"
          onClick={clearFilter} // bad-function
          className="defualt-padding-left"
        >
          <PlusSquare size={18} />

          {t("cleanAndCreateFlter")}
        </button>
      )}
    </form>
  );
};

export default MultiSelect;
